import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { C, F, G, SH } from '../styles/tokens';

const SERVICES = [
  {
    title: 'Private Limited Company',
    desc: 'Most popular structure for startups and growing businesses. Offers limited liability, separate legal identity, and easy fundraising from investors.',
    keywords: 'Pvt Ltd registration, incorporate private limited company India',
  },
  {
    title: 'Limited Liability Partnership (LLP)',
    desc: 'Combines the benefits of a partnership firm and a company. Ideal for professionals, consultancies, and small businesses wanting low compliance costs.',
    keywords: 'LLP registration online, LLP incorporation India',
  },
  {
    title: 'One Person Company (OPC)',
    desc: 'Designed for solo entrepreneurs who want corporate status without a co-founder. Full limited liability protection with minimal compliance burden.',
    keywords: 'OPC registration, one person company India',
  },
  {
    title: 'Proprietorship',
    desc: 'Simplest and fastest business structure. Ideal for freelancers and micro-businesses with a single owner managing all operations.',
    keywords: 'sole proprietorship registration India',
  },
  {
    title: 'Section 8 Company (NGO)',
    desc: 'For non-profit organizations working in education, charity, arts, or social welfare. Eligible for tax exemptions and grants.',
    keywords: 'NGO registration India, Section 8 company registration',
  },
  {
    title: 'Partnership Firm',
    desc: 'Traditional structure for two or more partners running a business together. Simple registration with a partnership deed under the Indian Partnership Act.',
    keywords: 'partnership firm registration India',
  },
];

const STEPS = [
  { step: '01', title: 'Free Consultation', desc: 'Discuss your business type, goals, and ideal structure with our experts — absolutely free.' },
  { step: '02', title: 'Document Collection', desc: 'We guide you on the exact documents needed: PAN, Aadhaar, address proof, and photos.' },
  { step: '03', title: 'Name Approval', desc: 'We apply for your company name through MCA and ensure it passes availability and trademark checks.' },
  { step: '04', title: 'DSC & DIN', desc: 'We obtain Digital Signature Certificates (DSC) and Director Identification Numbers (DIN) for all directors.' },
  { step: '05', title: 'MOA & AOA Drafting', desc: 'Our CS professionals draft the Memorandum of Association and Articles of Association tailored to your business.' },
  { step: '06', title: 'Certificate of Incorporation', desc: 'We file SPICe+ on MCA and you receive your Certificate of Incorporation, PAN, TAN, and bank account opening letter.' },
];

const FAQS = [
  {
    q: 'How long does company registration take in India?',
    a: 'Typically 7–10 working days for a Private Limited Company after all documents are submitted. LLP registration takes 5–7 working days. OPC can be completed in 5–7 days as well.',
  },
  {
    q: 'What is the minimum capital required to register a Pvt Ltd company?',
    a: 'There is no minimum paid-up capital requirement for incorporating a Private Limited Company in India after the Companies Act 2013 amendment. You can start with as little as ₹1,000.',
  },
  {
    q: 'What documents are required for company registration in India?',
    a: 'PAN card of all directors, Aadhaar card / passport, latest bank statement or utility bill (address proof), passport-size photographs, and a NOC from the property owner for the registered office address.',
  },
  {
    q: 'Can NRIs or foreign nationals register a company in India?',
    a: 'Yes. NRIs and foreign nationals can be directors in an Indian company. At least one director must be a resident Indian (person who has stayed in India for at least 182 days in the previous calendar year).',
  },
  {
    q: 'What is the difference between LLP and Private Limited Company?',
    a: 'A Private Limited Company is better suited for businesses that plan to raise equity funding (angel/VC) since it allows issuing shares. An LLP has lower annual compliance costs and is preferred by professional services firms. Both offer limited liability protection.',
  },
  {
    q: 'What is SPICe+ form in company registration?',
    a: 'SPICe+ (Simplified Proforma for Incorporating Company Electronically Plus) is the MCA portal form used to incorporate companies in India. It combines name reservation, incorporation, DIN allotment, PAN, TAN, EPFO, ESIC, and bank account opening in a single application.',
  },
];

const CompanyRegistrationPage = () => (
  <>
    <Helmet>
      <title>Company Registration in India in 7 Days — Pvt Ltd, LLP, OPC | Pracco</title>
      <meta name="description" content="Register your company in India in just 7 days with Pracco. Private Limited, LLP, OPC & Proprietorship. Expert CA & CS support. 100% online. Free consultation — call +91-9311817126." />
      <meta name="keywords" content="company registration India, private limited company registration, new company incorporation India, LLP registration, OPC registration, business registration online, incorporate company India, Pvt Ltd registration, SPICe+ filing" />
      <link rel="canonical" href="https://pracco.in/company-registration" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pracco.in/" },
          { "@type": "ListItem", "position": 2, "name": "Company Registration", "item": "https://pracco.in/company-registration" },
        ],
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Register a Company in India",
        "description": "Step-by-step process to register a Private Limited Company, LLP, or OPC in India online.",
        "totalTime": "P7D",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": parseInt(s.step),
          "name": s.title,
          "text": s.desc,
        })),
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      })}</script>
    </Helmet>

    {/* ── Mini Header ── */}
    <Box component="header" sx={{ px: { xs: 2, md: 6 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <Box component="img" src={logo} alt="Pracco" sx={{ height: 36 }} />
      </Link>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 600,
          fontSize: 13,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { background: C.whatsappDk },
        }}
      >
        Free Consultation
      </Button>
    </Box>

    {/* ── Hero ── */}
    <Box
      component="section"
      sx={{
        background: G.hero,
        px: { xs: 2.5, md: 8 },
        py: { xs: 6, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography
        component="h1"
        sx={{ fontFamily: F.heading, fontSize: { xs: 28, md: 44 }, fontWeight: 800, color: C.text, lineHeight: 1.15, mb: 2 }}
      >
        Company Registration in India
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: { xs: 15, md: 18 }, color: C.textMid, maxWidth: 680, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
        Incorporate your business online — Private Limited, LLP, OPC, or Proprietorship. Expert CA &amp; CS support.
        Fast 7-day registration. 100% online. Free consultation included.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          component="a"
          href="https://wa.me/9311817126"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={{
            background: G.blue,
            color: C.white,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 15,
            px: 4,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            boxShadow: SH.btnBlue,
            '&:hover': { boxShadow: SH.btnBlueHover },
          }}
        >
          Get Free Consultation
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          sx={{
            fontFamily: F.body,
            fontWeight: 600,
            fontSize: 14,
            px: 3,
            py: 1.5,
            borderRadius: 2.5,
            textTransform: 'none',
            borderColor: C.blue,
            color: C.blue,
            '&:hover': { background: C.blueLight },
          }}
        >
          View All Services
        </Button>
      </Box>
    </Box>

    {/* ── Service Types ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Types of Company Registration
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Choose the right structure for your business. Our experts will help you decide.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {SERVICES.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.title}>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, p: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: SH.cardHover } }}>
              <CheckCircleOutlineIcon sx={{ color: C.blue, mb: 1 }} />
              <Typography sx={{ fontFamily: F.heading, fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── Process Steps ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.bg }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        How Company Registration Works
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Our step-by-step process makes new company incorporation simple and hassle-free.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
        {STEPS.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.step}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: F.heading, fontSize: 34, fontWeight: 800, color: C.border, lineHeight: 1, minWidth: 44 }}>{s.step}</Typography>
              <Box>
                <Typography sx={{ fontFamily: F.heading, fontSize: 15, fontWeight: 700, color: C.text, mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── FAQ ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white, maxWidth: 860, mx: 'auto' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 4, textAlign: 'center' }}>
        Frequently Asked Questions — Company Registration
      </Typography>
      {FAQS.map((faq) => (
        <Accordion key={faq.q} disableGutters elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: C.blue }} />}>
            <Typography sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 15, color: C.text }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.8 }}>{faq.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* ── CTA ── */}
    <Box component="section" sx={{ background: G.navyBlue, px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 7 }, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.white, mb: 1.5 }}>
        Ready to Register Your Company?
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
        Talk to our experts today. Free consultation, transparent pricing, and 100% online process.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{
          background: C.whatsapp,
          color: C.white,
          fontFamily: F.body,
          fontWeight: 700,
          fontSize: 16,
          px: 5,
          py: 1.75,
          borderRadius: 2.5,
          textTransform: 'none',
          boxShadow: SH.btnGreen,
          '&:hover': { background: C.whatsappDk, boxShadow: SH.btnGreenHover },
        }}
      >
        WhatsApp Us Now
      </Button>
    </Box>

    {/* ── Mini Footer ── */}
    <Box component="footer" sx={{ px: { xs: 2.5, md: 8 }, py: 3, background: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} Pracco Corporate Services Pvt. Ltd. — info@pracco.in · +91-9311817126
      </Typography>
      <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        ← Back to Home
      </Link>
    </Box>
  </>
);

export default CompanyRegistrationPage;
